import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { getHealthColor } from './ScoreIndicator';
import { CHART_COLORS } from '../shared';
import './CrossSurveyComparison.css';

interface CategoryInfo {
  id: string;
  name: string;
  description: string;
}

interface ComparisonData {
  surveyType: string;
  percentPositive: number;
  averageScore: number;
  totalResponses: number;
  questionCount: number;
  questions: string[];
  healthIndicator: string;
}

interface CategoryComparison {
  category: CategoryInfo;
  comparison: ComparisonData[];
}

const surveyTypeLabels: Record<string, string> = {
  'k-2': 'K-2',
  '3-5': '3-5',
  '6-8': '6-8',
  'high-school': 'High School',
  'virtual-k8': 'Virtual K-8',
  'virtual-hs': 'Virtual HS',
};

interface CrossSurveyComparisonProps {
  respondentType?: string;
}

const CrossSurveyComparison: React.FC<CrossSurveyComparisonProps> = ({ respondentType = 'student' }) => {
  const { token } = useAuth();
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<CategoryComparison | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load available categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!token) return;

      setIsLoading(true);
      try {
        const response = await fetch(`/api/admin/stats/cross-survey?role=${respondentType}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Failed to fetch categories');

        const data = await response.json();
        setCategories(data.categories || []);
        if (data.categories?.length > 0) {
          setSelectedCategory(data.categories[0].id);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  // Load comparison data for selected category
  useEffect(() => {
    const fetchComparison = async () => {
      if (!token || !selectedCategory) return;

      setIsLoadingComparison(true);
      try {
        const response = await fetch(
          `/api/admin/stats/cross-survey?category=${selectedCategory}&role=${respondentType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to fetch comparison');

        const data = await response.json();
        setComparisonData(data);
      } catch (err: any) {
        console.error('Comparison fetch error:', err);
      } finally {
        setIsLoadingComparison(false);
      }
    };

    fetchComparison();
  }, [token, selectedCategory]);

  if (isLoading) {
    return (
      <div className="cross-survey__loading">
        <div className="cross-survey__spinner" />
        <p>Loading cross-survey categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cross-survey__error">
        <p>Error: {error}</p>
      </div>
    );
  }

  const chartData = comparisonData?.comparison.map((item) => ({
    name: surveyTypeLabels[item.surveyType] || item.surveyType,
    value: item.percentPositive,
    responses: item.totalResponses,
    questions: item.questionCount,
    color: getHealthColor(item.percentPositive),
  })) || [];

  return (
    <div className="cross-survey">
      <div className="cross-survey__header">
        <h2>Cross-Survey Category Comparison</h2>
        <p className="cross-survey__description">
          Compare similar concepts across different survey types to identify district-wide patterns
        </p>
      </div>

      <div className="cross-survey__controls">
        <label htmlFor="category-select">Select Category:</label>
        <select
          id="category-select"
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="cross-survey__select"
        >
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {comparisonData && (
        <div className="cross-survey__content">
          <div className="cross-survey__category-info">
            <h3>{comparisonData.category.name}</h3>
            <p>{comparisonData.category.description}</p>
          </div>

          {isLoadingComparison ? (
            <div className="cross-survey__chart-loading">
              <div className="cross-survey__spinner" />
            </div>
          ) : (
            <>
              <div className="cross-survey__chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: '#6d6d70' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 12, fill: '#6d6d70' }}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value}% Positive`,
                        props.payload.name,
                      ]}
                      labelFormatter={(label) => `Survey: ${label}`}
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #e5e5ea',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="cross-survey__table">
                <table>
                  <thead>
                    <tr>
                      <th>Survey Type</th>
                      <th>% Positive</th>
                      <th>Avg Score</th>
                      <th>Responses</th>
                      <th>Questions</th>
                      <th>Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.comparison.map((item) => (
                      <tr key={item.surveyType}>
                        <td className="survey-type-cell">
                          {surveyTypeLabels[item.surveyType] || item.surveyType}
                        </td>
                        <td className="score-cell">
                          <span style={{ color: getHealthColor(item.percentPositive) }}>
                            {Math.round(item.percentPositive)}%
                          </span>
                        </td>
                        <td className="score-cell">{item.averageScore.toFixed(1)}</td>
                        <td className="numeric-cell">~{item.totalResponses}</td>
                        <td className="numeric-cell">{item.questionCount}</td>
                        <td className="health-cell">
                          <span
                            className="health-badge"
                            style={{ backgroundColor: getHealthColor(item.percentPositive) }}
                          >
                            {item.healthIndicator}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cross-survey__insights">
                <h4>Insights</h4>
                {comparisonData.comparison.length > 0 && (
                  <ul>
                    {(() => {
                      const sorted = [...comparisonData.comparison].sort(
                        (a, b) => b.percentPositive - a.percentPositive
                      );
                      const highest = sorted[0];
                      const lowest = sorted[sorted.length - 1];
                      const spread = highest.percentPositive - lowest.percentPositive;

                      return (
                        <>
                          <li>
                            <strong>{surveyTypeLabels[highest.surveyType]}</strong> has the highest{' '}
                            {comparisonData.category.name.toLowerCase()} scores at{' '}
                            {Math.round(highest.percentPositive)}% positive
                          </li>
                          {spread > 10 && (
                            <li>
                              There's a <strong>{Math.round(spread)}% gap</strong> between the highest and
                              lowest survey types - this may warrant investigation
                            </li>
                          )}
                          {lowest.percentPositive < 50 && (
                            <li className="concern">
                              <strong>{surveyTypeLabels[lowest.surveyType]}</strong> scores below 50% -
                              this is a concern area
                            </li>
                          )}
                        </>
                      );
                    })()}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CrossSurveyComparison;
